import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useSelector } from "react-redux";
import { Wallet, CheckCircle2, X, User, Briefcase } from "lucide-react";
import PageShell from "../../components/dashboard/PageShell";
import FilteredDataTable from "../../components/dashboard/FilteredDataTable";
import StatusBadge from "../../components/dashboard/StatusBadge";
import StatCard from "../../components/dashboard/StatCard";
import StatCardGrid from "../../components/dashboard/StatCardGrid";
import { PAYMENTS_FILTER_CONFIG } from "../../config/tableFilterConfigs";
import { MOCK_PAYMENTS, formatCurrency, formatPercent } from "../../data/mock/dashboardData";
import { USE_MOCK_AUTH } from "../../config/api";
import { fetchProviderPayments, fetchProviderPaymentsSummary } from "../../services/dashboardApi";
import { isPlaceholderName, personNameColumns } from "../../utils/personName";
import { showError } from "../../utils/toast";

export default function PaymentsPage() {
  const [searchParams] = useSearchParams();
  const rawCustomer = searchParams.get("customer");
  const customerParam = rawCustomer && !isPlaceholderName(rawCustomer) ? rawCustomer : null;
  const token = useSelector((state) => state.auth.token);
  const useLiveApi = Boolean(token) && !USE_MOCK_AUTH;
  const [rows, setRows] = useState(() => (useLiveApi ? [] : MOCK_PAYMENTS));
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(useLiveApi);

  useEffect(() => {
    if (!useLiveApi) {
      setRows(MOCK_PAYMENTS);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    Promise.allSettled([fetchProviderPayments(token), fetchProviderPaymentsSummary(token)])
      .then(([paymentsResult, summaryResult]) => {
        if (cancelled) return;
        if (paymentsResult.status === "fulfilled") {
          setRows(paymentsResult.value || []);
        } else {
          showError(paymentsResult.reason?.message || "Could not load payments.");
        }
        if (summaryResult.status === "fulfilled") {
          setSummary(summaryResult.value || null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, useLiveApi]);

  const successfulRows = useMemo(
    () =>
      rows.filter((payment) => {
        const status = String(payment.status || "").toLowerCase();
        return payment.reconciled || status === "completed" || status === "reconciled";
      }),
    [rows]
  );

  const visible = useMemo(() => {
    if (!customerParam) return successfulRows;
    return successfulRows.filter((p) => p.customer === customerParam);
  }, [successfulRows, customerParam]);

  const initialFilters = useMemo(() => {
    if (!customerParam) return undefined;
    return { customer: [customerParam] };
  }, [customerParam]);

  const totalCollected = useMemo(
    () =>
      visible.reduce(
        (sum, payment) => sum + (payment.status === "completed" ? Number(payment.amount) || 0 : 0),
        0
      ),
    [visible]
  );

  const paymentCompletionRate = Number(
    summary?.payment_completion ??
      (visible.length
        ? (visible.filter((p) => p.status === "completed").length / visible.length) * 100
        : 0)
  );

  const reconciliationRate = Number(
    summary?.collected_reconciliation_rate ??
      (visible.length ? (visible.filter((p) => p.reconciled).length / visible.length) * 100 : 0)
  );

  const paymentColumns = [
    { key: "receivedAt", header: "Date & time", type: "datetime" },
    ...personNameColumns(),
    { key: "amount", header: "Payment amount", type: "currency", className: "whitespace-nowrap" },
    { key: "numericId", header: "ID", type: "id" },
    { key: "caseId", header: "Case ID", type: "id" },
    { key: "amountOwed", header: "Amount owed", type: "currency" },
    { key: "amountPaid", header: "Amount paid", type: "currency" },
    { key: "outstandingAmount", header: "Outstanding", type: "currency" },
    { key: "paymentType", header: "Payment type", type: "channel" },
    { key: "network", header: "Network", type: "network" },
    { key: "msisdn", header: "Wallet", type: "id" },
    { key: "method", header: "Interaction method", type: "channel" },
    {
      key: "status",
      header: "Status",
      type: "badge",
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: "reconciled",
      header: "Reconciled",
      type: "badge",
      render: (row) => (
        <StatusBadge status={row.reconciled ? "matched" : "pending"} label={row.reconciled ? "Yes" : "Pending"} />
      ),
    },
    {
      key: "comment",
      header: "Comment",
      render: (row) => {
        const tags = row.commentPresets?.length ? row.commentPresets.join(" · ") : "";
        const text = row.comment || "";
        if (!tags && !text) return <span className="text-primary/35">—</span>;
        return (
          <div className="max-w-[220px]">
            {tags ? <p className="text-xs font-semibold text-primary/70">{tags}</p> : null}
            {text ? <p className="truncate text-xs text-primary/50">{text}</p> : null}
          </div>
        );
      },
    },
    { key: "reference", header: "Reference", type: "id" },
    {
      key: "actions",
      header: "Actions",
      align: "center",
      headerClassName: "text-center",
      actions: (row) => [
        {
          key: "cases",
          label: "View collection cases",
          icon: Briefcase,
          tone: "primary",
          to: row.customer
            ? `/dashboard/cases?customer=${encodeURIComponent(row.customer)}`
            : "/dashboard/cases",
        },
      ],
    },
  ];

  return (
    <PageShell
      title="Payments"
      description="Full, partial, scheduled and instalment payments collected across your portfolio."
    >
      {customerParam && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/10 bg-gradient-to-r from-primary/[0.04] to-accent/[0.06] px-4 py-3">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-primary shadow-sm">
              <User className="h-4 w-4" />
            </span>
            <div>
              <p className="text-xs font-semibold text-primary">Payments for</p>
              <p className="text-sm font-bold text-primary">{customerParam}</p>
            </div>
          </div>
          <Link
            to="/dashboard/payments"
            className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-primary/70 ring-1 ring-primary/10 transition-colors hover:text-primary"
          >
            <X className="h-3.5 w-3.5" />
            Show all payments
          </Link>
        </div>
      )}

      <StatCardGrid cols={3}>
        <StatCard
          label="Collected"
          value={loading ? "…" : formatCurrency(summary?.total_collected ?? totalCollected)}
          icon={Wallet}
          tone="primary"
        />
        <StatCard
          label="Reconciliation rate"
          value={loading ? "…" : formatPercent(reconciliationRate)}
          icon={CheckCircle2}
          tone="amber"
          progress={reconciliationRate}
        />
        <StatCard
          label="Payment completion"
          value={loading ? "…" : formatPercent(paymentCompletionRate)}
          icon={CheckCircle2}
          tone="emerald"
          progress={paymentCompletionRate}
        />
      </StatCardGrid>

      <FilteredDataTable
        key={`payments-${customerParam ?? "all"}-${loading ? "loading" : "ready"}`}
        data={visible}
        columns={paymentColumns}
        config={{ ...PAYMENTS_FILTER_CONFIG, title: "Payment filters" }}
        initialFilters={initialFilters}
        pageSize={10}
        maxVisibleColumns={6}
        emptyMessage={loading ? "Loading payments…" : "No successful payments yet."}
      />
    </PageShell>
  );
}
